import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getValidAccessToken, oktaAuth } from './oktaAuth';

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export interface IAxiosHttpConf {
  baseURL: string;
}

export class AxiosHttp {
  private readonly config: IAxiosHttpConf;

  private axiosInstance: AxiosInstance;

  constructor(config: IAxiosHttpConf) {
    this.config = config;
    this.axiosInstance = this.initAxios();
  }

  async get<ResponseType>(
    url: string,
    config: AxiosRequestConfig<null> | undefined = undefined
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.get<ResponseType, AxiosResponse<ResponseType>, null>(url, config);
  }

  async post<RequestType, ResponseType>(
    url: string,
    data?: RequestType
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.post<ResponseType, AxiosResponse<ResponseType>, RequestType>(
      url,
      data
    );
  }

  async put<RequestType, ResponseType>(
    url: string,
    data?: RequestType
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.put<ResponseType, AxiosResponse<ResponseType>, RequestType>(
      url,
      data
    );
  }

  async delete<ResponseType>(
    url: string,
    config: AxiosRequestConfig<any> | undefined = undefined
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.delete<ResponseType, AxiosResponse<ResponseType>, null>(url, config);
  }

  async patch<RequestType, ResponseType>(
    url: string,
    data?: RequestType
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.patch<ResponseType, AxiosResponse<ResponseType>, RequestType>(
      url,
      data
    );
  }

  async setHeader(header: string, value: string): Promise<void> {
    this.axiosInstance.defaults.headers.common[header] = value;
  }

  private initAxios(): AxiosInstance {
    const { baseURL } = this.config;
    const axiosInstance = axios.create({
      baseURL,
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
      },
    });
    // Request interceptor: attach a valid access token, renewing it first if it
    // has expired (or is about to). Reading the raw token from localStorage
    // could send a stale token and trigger a 401.
    axiosInstance.interceptors.request.use(
      async (config) => {
        const token = await getValidAccessToken();
        if (token) {
          config.headers['x-amz-authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: on 401 try to renew the token once and retry the
    // original request; if renewal fails, fall back to a full re-login instead
    // of failing silently.
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          error?.response?.status === 403 &&
          error?.response?.data?.message?.toLowerCase().includes('deactivated')
        ) {
          window.dispatchEvent(new CustomEvent('user-deactivated'));
          return Promise.reject(error);
        }

        const originalRequest = error?.config as RetriableRequestConfig | undefined;
        if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const renewed = await oktaAuth.tokenManager.renew('accessToken');
            const newToken = (renewed as { accessToken?: string } | undefined)?.accessToken;
            if (newToken) {
              originalRequest.headers['x-amz-authorization'] = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            }
          } catch {
            // fall through to re-login
          }
          await oktaAuth.signInWithRedirect();
        }

        return Promise.reject(error);
      }
    );

    return axiosInstance;
  }
}

export class Http {
  static axios: AxiosHttp;

  static init() {
    this.axios = new AxiosHttp({
      baseURL: process.env.REACT_APP_API_BASE_URL as string,
    });
  }
}
